import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Col, Modal, Row } from "react-bootstrap";
// i18n
import { useTranslation } from "react-i18next";
// Formik
import { Form, Formik } from "formik";
import * as Yup from "yup";
// Components
import FormSelect from "../../global/form/FormSelect";
import FieldInput from "../../global/form/FieldInput";
import FormCheck from "../../global/form/FormCheck";
import TextLoading from "../../global/TextLoading";
// API
import { UpdateClass } from "../../../api/class";

const customStyles = {
  addCustomOption: {
    color: "#247048",
  },
};

const ModalScoreFormat = ({ classId, show, onClose, onSave, scoreConfig }) => {
  const { t } = useTranslation(["class", "global"]);

  const formatsByAvg = [
    { value: "integer", label: t("class:modalScoreFormat.integerNumbers") },
    { value: "decimal", label: t("class:modalScoreFormat.decimalNumbers") },
  ];

  const formatsByPeriod = [
    ...formatsByAvg,
    {
      value: "customized",
      label: t("class:modalScoreFormat.customizedSystem"),
    },
  ];

  const presicionOptions = [
    { value: "1", label: "1" },
    { value: "2", label: "2" },
  ];

  // Rellena el array de obj con label y value del 100 al 1
  const scorePercentageOptions = [
    ...Array.from({ length: 100 }, (_, i) => ({
      label: (100 - i).toString() + " %",
      value: 100 - i,
    })),
  ];

  const customOption = { scoreLetter: "", miniumPercentage: "" };

  const [customSystemValues, setCustomSystemValues] = useState([
    { ...customOption },
  ]);
  const [isRounding, setIsRounding] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [currentValues, setCurrentValues] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (scoreConfig) {
      formatScoreConfig();
    }
  }, []);

  const formatScoreConfig = () => {
    const config = {
      scoreSystem: scoreConfig.score_system,
      gradePeriodFormat: formatsByPeriod.find(
        (format) => format.value == scoreConfig.grading_period_number_format,
      ),
      gradePeriodPrecision: presicionOptions.find(
        (option) => option.value == scoreConfig.grading_precision,
      ),
      averageFormat: formatsByAvg.find(
        (format) => format.value == scoreConfig.average_number_format,
      ),
      averagePrecision: presicionOptions.find(
        (option) => option.value == scoreConfig.average_precision,
      ),
      doRound: scoreConfig.average_rounding,
      averageRounding: scoreConfig.average_rounding
        ? scoreConfig.average_rounding_value?.toString()
        : "",
    };
    if (scoreConfig.score_system === "customized" && scoreConfig.equivalences) {
      const customValues = scoreConfig.equivalences.map((item) => ({
        scoreLetter: item.score_letter,
        miniumPercentage: {
          value: item.minimum_percentage,
          label: item.minimum_percentage + " %",
        },
      }));
      setCustomSystemValues(customValues);
    }
    setInitialValues(config);
  };

  const saveScoreConfig = (values) => {
    const payload = {
      id: classId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      score_config: formatValuesToSave(values),
    };
    setIsSaving(true);
    UpdateClass(payload)
      .then((response) => {
        if (response.status) {
          const { score_config } = response.data;
          onSave(score_config);
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const formatValuesToSave = ({
    scoreSystem,
    doRound,
    averagePrecision,
    averageFormat,
    gradePeriodFormat,
    gradePeriodPrecision,
    averageRounding,
  }) => {
    const isNumeric = scoreSystem !== "customized";
    return {
      score_system: scoreSystem,
      average_rounding: doRound,
      average_precision: averagePrecision?.value ?? (isNumeric ? 1 : undefined),
      average_number_format: averageFormat?.value,
      grading_period_number_format: gradePeriodFormat?.value,
      grading_precision: gradePeriodPrecision?.value ?? (isNumeric ? 1 : undefined),
      average_rounding_value: isNumeric ? (doRound ? (averageRounding || 0) : 0) : undefined,
      equivalences:
        gradePeriodFormat?.value === "customized"
          ? customSystemValues.map((item) => ({
              score_letter: item.scoreLetter,
              minimum_percentage: item.miniumPercentage.value,
            }))
          : [],
    };
  };

  return (
    <React.Fragment>
      <Modal
        show={show}
        id="modalScoreFormat"
        style={{ display: showConfirmationModal ? "none" : "block" }}
      >
        <Modal.Header>
          <Modal.Title id="titleScoreFormat">
            {t("class:modalScoreFormat.title")}
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={Yup.object().shape({
            averageRounding: isRounding
              ? Yup.string().test(
                  "valueTwoDesimals",
                  t("class:modalScoreFormat.enterValidValue"),
                  (value) => {
                    if (value) {
                      return /^\d{1,2}\.?\d{0,2}$/.test(value);
                    }
                  },
                )
              : null,
          })}
          onSubmit={saveScoreConfig}
        >
          {({ setFieldValue, values, dirty }) => (
            <Form id="formikFormScoreFormat">
              <Modal.Body>
                {/* Per period */}
                <Row>
                  <Col xs={6} className="mb-2">
                    <FormSelect
                      label={t("class:modalScoreFormat.numberEvaluationPeriod")}
                      name="gradePeriodFormat"
                      id="gradePeriodFormatSelect"
                      options={formatsByPeriod}
                      onChange={(event) => {
                        const scoreSystem =
                          event?.value === "customized"
                            ? "customized"
                            : "numeric";
                        setFieldValue("gradePeriodFormat", event);
                        setFieldValue("scoreSystem", scoreSystem);
                      }}
                      value={values.gradePeriodFormat}
                    />
                  </Col>
                  {values.gradePeriodFormat?.value === "decimal" && (
                    <Col xs={6} className="mb-2">
                      <FormSelect
                        label={t("class:modalScoreFormat.numberDecimalToUse")}
                        name="gradePeriodPrecision"
                        id="gradePeriodPrecisionSelect"
                        options={presicionOptions}
                        onChange={(event) => {
                          setFieldValue("gradePeriodPrecision", event);
                        }}
                        value={values.gradePeriodPrecision}
                      />
                    </Col>
                  )}
                </Row>
                {/* Custom System */}
                {values.gradePeriodFormat?.value === "customized" &&
                  customSystemValues.map((item, index) => (
                    <Row key={index}>
                      <Col xs={6} className="mb-2">
                        <FieldInput
                          label={t("class:modalScoreFormat.scoreLetter")}
                          placeholder={t("class:modalScoreFormat.scoreLetter")}
                          name={`scoreLetter${index + 1}`}
                          id={`scoreLetterInput${index + 1}`}
                          onChange={(e) => {
                            const newValues = [...customSystemValues];
                            const value = e.target.value.toUpperCase();
                            newValues[index].scoreLetter = value;
                            setCustomSystemValues(newValues);
                          }}
                          value={item.scoreLetter}
                        />
                      </Col>
                      <Col xs={5} className="mb-2">
                        <FormSelect
                          label={t("class:modalScoreFormat.scoreLimit")}
                          placeholder={"%"}
                          options={scorePercentageOptions}
                          name={`miniumPercentage${index + 1}`}
                          id={`miniumPercentageSelect${index + 1}`}
                          onChange={(event) => {
                            const newValues = [...customSystemValues];
                            newValues[index].miniumPercentage = event;
                            setCustomSystemValues(newValues);
                          }}
                          value={item.miniumPercentage}
                        />
                      </Col>
                      {index === 0 && (
                        <Col xs={1} className="mb-2 align-content-center">
                          {/* Icon plus to add new custom option */}
                          <i
                            className="bi bi-plus-circle-fill fa-lg"
                            style={customStyles.addCustomOption}
                            id="addCustomOptionIcon"
                            role="button"
                            onClick={() => {
                              setCustomSystemValues([
                                ...customSystemValues,
                                { ...customOption },
                              ]);
                            }}
                          />
                        </Col>
                      )}
                    </Row>
                  ))}
                {/* Per average */}
                <Row>
                  <Col xs={6} className="mb-2">
                    <FormSelect
                      label={t("class:modalScoreFormat.numberAverage")}
                      name="averageFormat"
                      id="averageFormatSelect"
                      options={formatsByAvg}
                      onChange={(event) => {
                        setFieldValue("averageFormat", event);
                      }}
                      value={values.averageFormat}
                    />
                  </Col>
                  {values.averageFormat?.value === "decimal" && (
                    <Col xs={6} className="mb-2">
                      <FormSelect
                        label={t("class:modalScoreFormat.numberDecimalToUse")}
                        name="averagePrecision"
                        id="averagePrecisionSelect"
                        options={presicionOptions}
                        onChange={(event) => {
                          setFieldValue("averagePrecision", event);
                        }}
                        value={values.averagePrecision}
                      />
                    </Col>
                  )}
                </Row>
                {/* Rounding Title */}
                <Row className="mb-2">
                  <Col
                    xs={12}
                    className="fs-6 fw-bold"
                    id="averageRoundingTitle"
                  >
                    {t("class:modalScoreFormat.averageRounding")}
                  </Col>
                </Row>
                {/* Rounding checks */}
                <Row>
                  <Col xs={6}>
                    <Row>
                      <Col xs={12} className="align-content-center">
                        <FormCheck
                          name="doRound"
                          id="roundingCheck"
                          label={t("class:modalScoreFormat.roundingMessage1")}
                          checked={values.doRound}
                          onChange={() => {
                            setFieldValue("doRound", true);
                            setIsRounding(true);
                          }}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        xs={{ span: 4, offset: 1 }}
                        id="roundingLabelMessage2"
                        className="pe-0"
                      >
                        {t("class:modalScoreFormat.roundingMessage2")}
                      </Col>
                      <Col xs={4} className="px-0">
                        <FieldInput
                          name="averageRounding"
                          id="averageRoundingInput"
                          type="text"
                          label={""}
                          placeholder=""
                          disabled={!values.doRound}
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={6}>
                    <FormCheck
                      name="doRound"
                      id="noRoundingCheck"
                      label={t("class:modalScoreFormat.noRounding")}
                      checked={!values.doRound}
                      onChange={() => {
                        setFieldValue("doRound", false);
                        setIsRounding(false);
                      }}
                    />
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <div id="scoreFormatFooter">
                  <Button
                    variant="secondary"
                    id="cancelScoreFormatBtn"
                    onClick={() => {
                      if (dirty) {
                        setShowConfirmationModal(true);
                        setCurrentValues(values);
                        return;
                      }
                      onClose();
                    }}
                    disabled={isSaving}
                  >
                    {t("global:buttons.cancel")}
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    id="saveScoreFormatBtn"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <TextLoading
                        variant="light"
                        text={t("global:buttons.saving")}
                      />
                    ) : (
                      t("global:buttons.save")
                    )}
                  </Button>
                </div>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <Modal show={showConfirmationModal} id="confirmScoreFormatModal">
          <Modal.Header>
            <Modal.Title id="confirmScoreFormatTitle">
              {t("global:warning")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body id="confirmScoreFormatBody">
            {t("global:unsavedChanges")}
          </Modal.Body>
          <Modal.Footer>
            <div id="confirmScoreFormatFooter">
              <Button
                variant="secondary"
                id="backToScoreFormatBtn"
                onClick={() => {
                  setShowConfirmationModal(false);
                }}
                disabled={isSaving}
              >
                {t("global:buttons.backTo2")}
              </Button>
              <Button
                variant="primary"
                id="saveWithoutChangesBtn"
                onClick={() => {
                  setShowConfirmationModal(false);
                  onClose();
                }}
                disabled={isSaving}
              >
                {t("global:buttons.exitWithoutSaving")}
              </Button>
              <Button
                id="saveChangesBtn"
                variant="primary"
                disabled={isSaving}
                onClick={() => {
                  saveScoreConfig(currentValues);
                }}
              >
                {isSaving ? (
                  <TextLoading
                    variant="light"
                    text={t("global:buttons.saving")}
                  />
                ) : (
                  t("global:buttons.save")
                )}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </React.Fragment>
  );
};

ModalScoreFormat.propTypes = {
  classId: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  scoreConfig: PropTypes.object,
};

ModalScoreFormat.defaultProps = {};

export default ModalScoreFormat;
